import apiService from './ApiService';
import { debugLog, debugError } from '../utils/debugUtils';
import i18n from '../i18n/config';
import firebaseMessagingService from './FirebaseMessagingService';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const REFRESH_TOKEN_KEY = 'concordare_refresh_token';

// Web: refresh token lives only in the httpOnly cookie (inaccessible to JS).
// Native: stored in Capacitor Preferences (Keychain on iOS, EncryptedSharedPreferences
// on Android) — encrypted at rest, isolated to this app.
const IS_NATIVE = Capacitor.isNativePlatform();

class AuthService {
  constructor() {
    // User is derived from the access token in memory — never from localStorage.
    // This eliminates the dichotomy where localStorage could belong to a different
    // account than the actual refresh token.
    this._user = null;
    // Remove any stale concordare_user that old builds may have left behind.
    localStorage.removeItem('concordare_user');
    // The refresh token must never live in localStorage.
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  // Decode a JWT access token to extract user fields without a network call.
  // The signature is NOT verified here (the backend already did that when issuing).
  _parseUser(accessToken) {
    if (!accessToken) return null;
    try {
      const payload = JSON.parse(
        atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
      );
      return {
        id: payload.id ?? null,
        email: payload.sub,
        name: payload.name || '',
        role: payload.role || null,
        language: payload.language || null,
      };
    } catch {
      return null;
    }
  }

  isAuthenticated() {
    return !!this._user;
  }

  getUser() {
    return this._user;
  }

  _isDebug() {
    return process.env.REACT_APP_DEBUG_MODE === 'true';
  }

  _mockUser(email, name) {
    return { id: 0, name: name || email?.split('@')[0] || 'Debug User', email: email || 'debug@concordare.dev' };
  }

  // Sets the in-memory user. Use the JWT-derived value when available so the
  // displayed user always matches the token holder. Falls back to an explicit
  // user object (e.g., from /users/me) when needed.
  _setUser(userOrToken) {
    if (!userOrToken) {
      this._user = null;
      return;
    }
    // If it looks like an access token string, decode it; otherwise treat as object.
    if (typeof userOrToken === 'string') {
      this._user = this._parseUser(userOrToken);
    } else {
      this._user = userOrToken;
    }
    if (this._user?.language) i18n.changeLanguage(this._user.language);
  }

  // Public method to update the in-memory user from an API response
  // (e.g., after updating the profile name within the current session).
  updateUser(user) {
    this._setUser(user);
  }

  async saveRefreshToken(token) {
    if (!token) return;
    if (IS_NATIVE) {
      await Preferences.set({ key: REFRESH_TOKEN_KEY, value: token });
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
    // On web: token lives only in the httpOnly cookie — nothing to store here
  }

  async getRefreshToken() {
    if (!IS_NATIVE) return null;
    // Preferences takes priority — prevents any stale localStorage entry from
    // overwriting a valid Preferences value.
    const { value } = await Preferences.get({ key: REFRESH_TOKEN_KEY });
    if (value) {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      return value;
    }
    // One-time migration: move old localStorage token to Preferences.
    const legacy = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (legacy) {
      await Preferences.set({ key: REFRESH_TOKEN_KEY, value: legacy });
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      return legacy;
    }
    return null;
  }

  async _clearRefreshToken() {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    if (IS_NATIVE) await Preferences.remove({ key: REFRESH_TOKEN_KEY });
  }

  async login(email, password) {
    if (this._isDebug()) {
      const mockUser = this._mockUser(email);
      apiService.setAccessToken('debug-access-token');
      this._setUser(mockUser);
      debugLog('[AuthService] DEBUG MODE — login bypassed with mock user', mockUser);
      return mockUser;
    }
    try {
      const data = await apiService.post('/api/v1/auth/login', { email, password }, false);
      apiService.setAccessToken(data.access_token);
      this._setUser(data.access_token);
      await this.saveRefreshToken(data.refresh_token);
      firebaseMessagingService.registerToken();
      debugLog('[AuthService] Login successful', this._user);
      return this._user;
    } catch (err) {
      debugError('[AuthService] Login failed:', err);
      throw err;
    }
  }

  async register(name, email, password, honeypot = '') {
    if (this._isDebug()) {
      debugLog('[AuthService] DEBUG MODE — register bypassed, simulating email sent');
      return { emailSent: true };
    }
    try {
      const data = await apiService.post(
        '/api/v1/auth/register',
        { name, email, password, website: honeypot },
        false
      );
      debugLog('[AuthService] Register successful, verification email sent');
      return data;
    } catch (err) {
      debugError('[AuthService] Register failed:', err);
      throw err;
    }
  }

  async verifyEmail(token) {
    try {
      const data = await apiService.get(`/api/v1/auth/verify-email?token=${encodeURIComponent(token)}`, false);
      apiService.setAccessToken(data.access_token);
      this._setUser(data.access_token);
      await this.saveRefreshToken(data.refresh_token);
      debugLog('[AuthService] Email verified, user logged in', this._user);
      return this._user;
    } catch (err) {
      debugError('[AuthService] Email verification failed:', err);
      throw err;
    }
  }

  async logout() {
    try {
      await apiService.post('/api/v1/auth/logout', null);
    } catch {
      // ignore server-side error; always clear locally
    } finally {
      apiService.clearTokens();
      this._user = null;
      await this._clearRefreshToken();
      debugLog('[AuthService] Logged out');
    }
  }

  async deleteAccount() {
    await apiService.delete('/api/v1/users/me');
    apiService.clearTokens();
    this._user = null;
    await this._clearRefreshToken();
    debugLog('[AuthService] Account deleted');
  }

  async silentRefresh() {
    if (this._isDebug()) return true;
    try {
      const storedRefreshToken = await this.getRefreshToken();
      const body = storedRefreshToken ? { refresh_token: storedRefreshToken } : null;
      const data = await apiService.post('/api/v1/auth/refresh-token', body, false);
      apiService.setAccessToken(data.access_token);
      // Derive user directly from the new token — single source of truth, no /users/me needed.
      this._setUser(data.access_token);
      await this.saveRefreshToken(data.refresh_token);
      firebaseMessagingService.registerToken();
      debugLog('[AuthService] Silent refresh successful', this._user);
      return true;
    } catch (err) {
      const status = err?.status;
      if (status >= 400 && status < 500) {
        debugError('[AuthService] Silent refresh rejected — clearing session', err);
        this._user = null;
        await this._clearRefreshToken();
      } else {
        debugError('[AuthService] Silent refresh failed (network/server error) — keeping session', err);
      }
      return false;
    }
  }

  async forgotPassword(email) {
    if (this._isDebug()) {
      debugLog('[AuthService] DEBUG MODE — forgotPassword bypassed');
      return;
    }
    try {
      await apiService.post('/api/v1/auth/forgot-password', { email }, false);
    } catch (err) {
      debugError('[AuthService] forgotPassword failed:', err);
      throw err;
    }
  }

  async resetPassword(token, newPassword) {
    if (this._isDebug()) {
      debugLog('[AuthService] DEBUG MODE — resetPassword bypassed');
      return;
    }
    try {
      await apiService.post('/api/v1/auth/reset-password', { token, newPassword }, false);
    } catch (err) {
      debugError('[AuthService] resetPassword failed:', err);
      throw err;
    }
  }

  async refreshMe() {
    try {
      const user = await apiService.get('/api/v1/users/me');
      this._setUser(user);
      firebaseMessagingService.registerToken();
      return user;
    } catch (err) {
      debugError('[AuthService] refreshMe failed:', err);
      throw err;
    }
  }
}

const authService = new AuthService();

// Wire up token handlers so ApiService uses Capacitor Preferences on native
// (avoids circular imports — ApiService is imported by AuthService, not the other way)
apiService.setTokenHandlers(
  () => authService.getRefreshToken(),
  (token) => authService.saveRefreshToken(token)
);

export default authService;
